import { useState, useRef } from "react";
import { Loader2, CheckCircle2, XCircle, SkipForward, Printer, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useActiveShippingCredentials, useShippingCredentials } from "@/hooks/use-shipping";
import { useQueryClient } from "@tanstack/react-query";
import { useGenerateLabels } from "@/hooks/use-fulfillment";
import { groupByStore } from "@/lib/shipping/group-credentials";
import type { ImportResult as ServerImportResult } from "@/services/shipping";

type ImportResult = ServerImportResult & { durationMs?: number };

// Server sends a heartbeat event every 15s; if 35s pass without any event,
// the stream is considered dead and we abort. Catches dropped connections
// that fetch+ReadableStream won't surface on its own.
const STALE_STREAM_TIMEOUT_MS = 35_000;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrderIds: string[];
  storeId: string;
  onSuccess: () => void;
  /** Odakle se biraju kurirski nalozi. "store" = nalozi te trgovine (maloprodaja);
   *  "all" = svi aktivni nalozi, grupisani po trgovini (veleprodaja, čije narudžbe
   *  ne pripadaju nijednoj trgovini). */
  credentialSource?: "store" | "all";
  /** Queryji koje treba osvježiti nakon uvoza, uz podrazumijevane liste narudžbi. */
  invalidateKeys?: string[][];
};

export function BulkImportDialog({ open, onOpenChange, selectedOrderIds, storeId, onSuccess, credentialSource = "store", invalidateKeys }: Props) {
  const allCredentials = useActiveShippingCredentials(credentialSource === "all");
  const storeCredentials = useShippingCredentials(credentialSource === "store" ? storeId : null);
  const { data: credentials, isLoading: credLoading } = credentialSource === "all" ? allCredentials : storeCredentials;
  const qc = useQueryClient();
  const generateLabels = useGenerateLabels();

  const [credentialId, setCredentialId] = useState("");
  const [numberOfPackages, setNumberOfPackages] = useState(1);
  const [allowOpening, setAllowOpening] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [importing, setImporting] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [done, setDone] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const cancelRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const total = selectedOrderIds.length;

  function handleClose() {
    if (importing) return;
    setResults([]);
    setCredentialId("");
    setNumberOfPackages(1);
    setAllowOpening(false);
    setProcessed(0);
    setDone(false);
    setCurrentOrderId(null);
    cancelRef.current = false;
    abortRef.current = null;
    onOpenChange(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setImporting(true);
    setResults([]);
    setProcessed(0);
    setDone(false);
    cancelRef.current = false;

    const controller = new AbortController();
    abortRef.current = controller;
    const orderStartedAt = new Map<string, number>();

    // Backend heartbeats every 15s; if 35s pass without any event the connection is
    // considered dead and we abort so the user gets an error instead of a frozen bar.
    let staleTimer: ReturnType<typeof setTimeout> | null = null;
    const resetStaleTimer = () => {
      if (staleTimer) clearTimeout(staleTimer);
      staleTimer = setTimeout(() => {
        controller.abort(new Error("Veza prema serveru je prekinuta (nema odgovora 35s)"));
      }, STALE_STREAM_TIMEOUT_MS);
    };

    let fatalError: string | null = null;

    try {
      const res = await fetch("/api/shipping/import/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderIds: selectedOrderIds,
          storeId,
          credentialId,
          numberOfPackages,
          allowOpening,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error || res.statusText);
      }

      resetStaleTimer();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        resetStaleTimer();

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // Keep the trailing incomplete line (if any) for the next chunk.
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.trim()) continue;
          let event: any;
          try { event = JSON.parse(line); } catch { continue; }

          switch (event.type) {
            case "heartbeat":
              break; // stale timer already reset above
            case "processing":
              setCurrentOrderId(event.orderId);
              orderStartedAt.set(event.orderId, Date.now());
              break;
            case "result": {
              const startedAt = orderStartedAt.get(event.result.orderId);
              const durationMs = startedAt ? Date.now() - startedAt : undefined;
              setResults((prev) => [...prev, { ...event.result, durationMs }]);
              if (typeof event.index === "number") setProcessed(event.index + 1);
              break;
            }
            case "done":
              // Loop ends when the server closes the stream after this.
              break;
            case "error":
              fatalError = event.error || "Greška na serveru";
              break;
            default:
              break;
          }
        }
      }
    } catch (err: any) {
      const aborted = err?.name === "AbortError" || controller.signal.aborted;
      fatalError = aborted
        ? (cancelRef.current ? "Otkazano" : (err?.message || "Veza prekinuta"))
        : (err?.message || "Mrežna greška");
    } finally {
      if (staleTimer) clearTimeout(staleTimer);
      abortRef.current = null;
      setCurrentOrderId(null);
    }

    if (fatalError) {
      setResults((prev) => [...prev, {
        orderId: "",
        orderNumber: "—",
        success: false,
        error: fatalError!,
      }]);
    }

    // Flip out of the "sending" state first so a failed refetch can't trap the dialog.
    setImporting(false);
    setDone(true);
    try {
      // Badge on the orders list reads from order-shipments-bulk; refresh both so
      // newly-imported shipments show up immediately without a manual reload.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["orders"] }),
        qc.invalidateQueries({ queryKey: ["order-shipments-bulk"] }),
        ...(invalidateKeys ?? []).map((key) => qc.invalidateQueries({ queryKey: key })),
      ]);
    } catch {
      // ignore — the list will refresh on next navigation
    }
    onSuccess();
  }

  function handleCancel() {
    cancelRef.current = true;
    // Abort the in-flight request so the user doesn't wait the full timeout.
    abortRef.current?.abort(new Error("Otkazano"));
  }

  const successCount = results.filter((r) => r.success).length;
  const failedCount = results.filter((r) => !r.success && !r.skipped).length;
  const skippedCount = results.filter((r) => r.skipped).length;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => { if (importing) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Pošalji kuriru</DialogTitle>
          <DialogDescription>
            {selectedOrderIds.length} narudžbi odabrano
          </DialogDescription>
        </DialogHeader>

        {(importing || done) ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {importing
                    ? (currentOrderId ? `Slanje: #${currentOrderId}` : "Slanje u toku...")
                    : "Završeno"}
                </span>
                <span className="font-medium">{processed} / {total}</span>
              </div>
              <Progress value={percent} />
            </div>

            {results.length > 0 && (
              <div className="flex gap-2">
                {successCount > 0 && <Badge className="bg-green-500/10 text-green-600 border-green-500/20" variant="outline">{successCount} uspješno</Badge>}
                {failedCount > 0 && <Badge className="bg-red-500/10 text-red-600 border-red-500/20" variant="outline">{failedCount} neuspješno</Badge>}
                {skippedCount > 0 && <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20" variant="outline">{skippedCount} preskočeno</Badge>}
              </div>
            )}

            <div className="flex flex-col gap-1 max-h-60 overflow-y-auto text-xs">
              {results.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 p-1.5 border-b last:border-0">
                  {r.warning ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  ) : r.success ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                  ) : r.skipped ? (
                    <SkipForward className="h-3.5 w-3.5 text-yellow-600 shrink-0" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />
                  )}
                  <span className="font-medium">#{r.orderNumber}</span>
                  {r.trackingNumber && <span className="text-muted-foreground">— {r.trackingNumber}</span>}
                  {r.error && <span className="text-red-600">— {r.error}</span>}
                  {r.warning && <span className="text-amber-600">— {r.warning}</span>}
                  {typeof r.durationMs === "number" && (
                    <span className="text-muted-foreground ml-auto">{(r.durationMs / 1000).toFixed(1)}s</span>
                  )}
                </div>
              ))}
            </div>

            <DialogFooter>
              {importing ? (
                <Button variant="outline" onClick={handleCancel}>Otkaži</Button>
              ) : (
                <div className="flex gap-2">
                  {successCount > 0 && (
                    <Button
                      variant="outline"
                      className="gap-2"
                      disabled={generateLabels.isPending}
                      onClick={() => {
                        const successIds = results.filter((r) => r.success).map((r) => r.orderId);
                        if (successIds.length === 0) return;
                        generateLabels.mutate({ orderIds: successIds, storeId });
                      }}
                    >
                      {generateLabels.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                      Printaj naljepnice ({successCount})
                    </Button>
                  )}
                  <Button onClick={handleClose}>Zatvori</Button>
                </div>
              )}
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {credLoading ? (
              <p className="text-xs text-muted-foreground">Učitavanje...</p>
            ) : !credentials?.length ? (
              <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 text-xs text-yellow-700">
                {credentialSource === "all"
                  ? "Nema aktivnih kurirskih naloga. Dodajte nalog u Postavkama."
                  : "Nema konfiguriranih kurirskih naloga za ovu trgovinu. Dodajte nalog u Postavkama."}
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label>Kurirska služba</Label>
                  <Select value={credentialId} onValueChange={setCredentialId}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="Odaberi" /></SelectTrigger>
                    <SelectContent>
                      {credentialSource === "all"
                        ? groupByStore(credentials).map(([storeName, group]) => (
                            <SelectGroup key={storeName || "—"}>
                              <SelectLabel>{storeName || "Bez trgovine"}</SelectLabel>
                              {group.map((c: any) => (
                                <SelectItem key={c.id} value={c.id}>
                                  <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: c.color }} />
                                    {c.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))
                        : credentials.map((c: any) => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: c.color }} />
                                {c.name}
                              </div>
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>Broj paketa</Label>
                  <Input type="number" min={1} value={numberOfPackages} onChange={(e) => setNumberOfPackages(Number(e.target.value))} />
                </div>

                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={allowOpening} onCheckedChange={(v) => setAllowOpening(!!v)} />
                  Dozvoli otvaranje pošiljke
                </label>

                <DialogFooter>
                  <Button type="submit" disabled={!credentialId}>
                    Pošalji ({selectedOrderIds.length})
                  </Button>
                </DialogFooter>
              </>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
